export type ImportKind = 'repetidas' | 'faltantes-reverse' | 'tenho';

export interface ParseResult {
  parsed: Map<string, number>;
  // repetidas: valor = count total desejado (extras + 1)
  // faltantes-reverse: valor sempre 0 — Map serve só como Set de "está na lista"
  unrecognized: string[];
  conflictingHeader: 'faltantes' | 'repetidas' | null;
}

const MAX_UNRECOGNIZED = 50;

function normalize(input: string): string {
  return input
    .replace(/^﻿/, '')
    .replace(/ /g, ' ')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
}

function setMax(map: Map<string, number>, code: string, count: number) {
  const existing = map.get(code);
  if (existing === undefined || count > existing) map.set(code, count);
}

function buildCode(teamPrefix: string, raw: string): string | null {
  const team = teamPrefix.toUpperCase();
  if (team === 'FWC') {
    if (raw.replace(/\s/g, '') === '00') return 'FWC00';
    const num = parseInt(raw, 10);
    if (Number.isNaN(num) || num <= 0) return null;
    return `FWC${num}`;
  }
  const num = parseInt(raw, 10);
  if (Number.isNaN(num) || num <= 0) return null;
  return `${team}${num}`;
}

function collectUnrecognized(set: Set<string>, token: string) {
  if (set.size >= MAX_UNRECOGNIZED) return;
  set.add(token);
}

function parseFaltantesReverseBody(
  body: string,
  validCodes: Set<string>,
  parsed: Map<string, number>,
  unrecognized: Set<string>
) {
  const consumed: Array<[number, number]> = [];

  // Format A: linhas com prefixo de time + emoji opcional + ":" + lista de números
  const lineRegex = /^[^\S\n]*([A-Za-z]{2,4})[^\w\n:]*:\s*([0-9,\s]+)$/gm;
  let lm: RegExpExecArray | null;
  while ((lm = lineRegex.exec(body)) !== null) {
    consumed.push([lm.index, lm.index + lm[0].length]);
    const team = lm[1].toUpperCase();
    const numbers = lm[2].split(',').map((s) => s.trim()).filter(Boolean);
    for (const raw of numbers) {
      const code = buildCode(team, raw);
      if (code && validCodes.has(code)) setMax(parsed, code, 0);
      else if (code) collectUnrecognized(unrecognized, code);
      else collectUnrecognized(unrecognized, `${team}${raw}`);
    }
  }

  // Format D: códigos bare fora das linhas já consumidas
  const codeRegex = /([A-Za-z]{2,4})(\d{1,3})/g;
  let cm: RegExpExecArray | null;
  while ((cm = codeRegex.exec(body)) !== null) {
    const start = cm.index;
    if (consumed.some(([a, b]) => start >= a && start < b)) continue;
    const team = cm[1].toUpperCase();
    const numStr = cm[2];
    const code = buildCode(team, numStr);
    if (code && validCodes.has(code)) setMax(parsed, code, 0);
    else if (code) collectUnrecognized(unrecognized, code);
  }
}

function parseRepetidasBody(
  body: string,
  validCodes: Set<string>,
  parsed: Map<string, number>,
  unrecognized: Set<string>
) {
  const consumed: Array<[number, number]> = [];

  // Format B: "TEAM N(×M)" com espaço entre time e número
  const formatBRegex = /([A-Za-z]{2,4})\s+(\d{1,2})\s*\(\s*[xX×]\s*(\d+)\s*\)/g;
  let bm: RegExpExecArray | null;
  while ((bm = formatBRegex.exec(body)) !== null) {
    consumed.push([bm.index, bm.index + bm[0].length]);
    const code = buildCode(bm[1], bm[2]);
    const desired = parseInt(bm[3], 10) + 1;
    if (code && validCodes.has(code)) setMax(parsed, code, desired);
    else if (code) collectUnrecognized(unrecognized, code);
  }

  // Format C: "TEAMNUM xN?" colado
  const formatCRegex = /([A-Za-z]{2,4})(\d{1,3})(?:\s*[xX]\s*(\d+))?/g;
  let cm: RegExpExecArray | null;
  while ((cm = formatCRegex.exec(body)) !== null) {
    const start = cm.index;
    if (consumed.some(([a, b]) => start >= a && start < b)) continue;
    const extras = cm[3] ? parseInt(cm[3], 10) : 1;
    const code = buildCode(cm[1], cm[2]);
    const desired = extras + 1;
    if (code && validCodes.has(code)) setMax(parsed, code, desired);
    else if (code) collectUnrecognized(unrecognized, code);
  }

  // Extras nominados (REGU, BRON, PRAT, OURO) com xN opcional
  const wordRegex = /\b(REGU|BRON|PRAT|OURO)(?:\s*[xX]\s*(\d+))?\b/gi;
  let wm: RegExpExecArray | null;
  while ((wm = wordRegex.exec(body)) !== null) {
    const start = wm.index;
    if (consumed.some(([a, b]) => start >= a && start < b)) continue;
    const code = wm[1].toUpperCase();
    const extras = wm[2] ? parseInt(wm[2], 10) : 1;
    if (validCodes.has(code)) setMax(parsed, code, extras + 1);
  }
}

function detectConflictingHeader(
  normalizedText: string,
  kind: ImportKind
): 'faltantes' | 'repetidas' | null {
  const hasFaltantes = /\b(faltantes|faltam)\b/i.test(normalizedText);
  const hasRepetidas = /\b(repetidas|repetidos|trocaveis|possuo)\b/i.test(normalizedText);
  if ((kind === 'repetidas' || kind === 'tenho') && hasFaltantes) return 'faltantes';
  if (kind === 'faltantes-reverse' && hasRepetidas) return 'repetidas';
  return null;
}

export function parseStickerImport(
  text: string,
  validCodes: Set<string>,
  kind: ImportKind
): ParseResult {
  const parsed = new Map<string, number>();
  const unrecognized = new Set<string>();

  const normalized = normalize(text);
  if (!normalized.trim()) {
    return { parsed, unrecognized: [], conflictingHeader: null };
  }

  const conflictingHeader = detectConflictingHeader(normalized, kind);

  if (kind === 'repetidas') {
    parseRepetidasBody(normalized, validCodes, parsed, unrecognized);
  } else if (kind === 'tenho') {
    // Extrai os mesmos códigos que o modo Repetidas, mas ignora os multiplicadores.
    // Qualquer x2/x3 na lista é descartado: todos os códigos encontrados viram count=1.
    parseRepetidasBody(normalized, validCodes, parsed, unrecognized);
    for (const code of Array.from(parsed.keys())) {
      parsed.set(code, 1);
    }
  } else {
    parseFaltantesReverseBody(normalized, validCodes, parsed, unrecognized);
  }

  return {
    parsed,
    unrecognized: Array.from(unrecognized),
    conflictingHeader,
  };
}
